<template>
  <section class="menu computer">
    <div class="navigation">
      <div class="top-nav"></div>
      <div class="song-info-box" data-tour="sheet-info-panel">
        <div class="song">
          <h1>Názov piesne</h1>
          <h5>{{ zapis.nazov || "Názov" }}</h5>

          <div class="line horizontal w-80"></div>

          <div class="difficulty-row">
            <div class="dificulty">
              <img src="@/assets/images/obtiaznost/obtiaznost2.png" alt="" />
            </div>
            <div
              v-if="canPrint"
              class="sheet-print"
              role="button"
              tabindex="0"
              @click="onPrintClick"
              @keydown.enter.prevent="onPrintClick"
              @keydown.space.prevent="onPrintClick"
            >
              <div class="tlac-img" @click.stop="onPrintClick">
                <img :src="printIcon" alt="" @click.stop="onPrintClick" />
              </div>
              <p class="print-label">{{ $t("classroom.sheet.print") }}</p>
            </div>
          </div>

          <div class="info">
            <p class="strong">ČÍSELNÝ ZÁPIS NAPÍSAL:</p>
            <p class="info-text">{{ zapis.autor || "Autor" }}</p>
          </div>

          <!-- if none, also show selectable scales -->
          <div class="info-box">
            <div class="info">
              <p class="strong">TEMPO HRANIA:</p>
              <div class="bmp-box">
                <img src="@/assets/images/icons/metronom.svg" alt="" />
                <p class="info-text bmp">{{ zapis.tempo || "" }}</p>
              </div>
            </div>
            <div class="line"></div>
            <div class="info">
              <p class="strong">ŽÁNER PIESNE:</p>
              <p class="info-text">{{ genreLabel }}</p>
            </div>
          </div>
          <div class="info-buttons" data-tour="sheet-scale-picker">
            <div
              v-for="scale in availableScales"
              :key="scale"
              class="button Adumu"
              :class="{ green: isActiveScale(scale) }"
              role="button"
              tabindex="0"
              :aria-pressed="isActiveScale(scale) ? 'true' : 'false'"
              @click="selectScale(scale)"
              @keydown.enter.prevent="selectScale(scale)"
              @keydown.space.prevent="selectScale(scale)"
            >
              <p>{{ getScaleLabel(scale) }}</p>
            </div>
          </div>
          <div class="line horizontal w-80 last-line"></div>
        </div>
        <div class="info">
          <p class="strong">TEXT PIESNE:</p>
          <div class="song-text-wrapper">
            <p class="text-song">{{ zapis.text || "" }}</p>
          </div>
        </div>
      </div>

      <div class="bot-nav">
        <div class="zoom-controls">
          <button
            type="button"
            class="zoom-toggle"
            data-tour="sheet-favorite-toggle"
            @click="toggleFavorite"
            :aria-pressed="isFavorite"
            :aria-label="
              isFavorite ? 'Odstrániť z obľúbených' : 'Pridať do obľúbených'
            "
          >
            <img :src="favoriteIcon" alt="" />
          </button>
          <div
            class="zoom-panel"
            role="group"
            aria-label="Ovládanie priblíženia"
            data-tour="sheet-zoom-controls"
          >
            <button
              type="button"
              class="zoom-edge left"
              @click="zoomOut"
              aria-label="Oddialiť zápis"
            >
              −
            </button>
            <span class="zoom-value">{{ zoomLabel }}</span>
            <button
              type="button"
              class="zoom-edge right"
              @click="zoomIn"
              aria-label="Priblížiť zápis"
            >
              +
            </button>
          </div>
          <button
            type="button"
            class="zoom-toggle"
            data-tour="sheet-completion-toggle"
            @click="toggleCompleted"
            :aria-pressed="isCompleted"
            :aria-label="
              isCompleted ? 'Odstrániť zo zvládnutých' : 'Pridať do zvládnutých'
            "
          >
            <img :src="completedIcon" alt="" />
          </button>
        </div>
      </div>

      <router-link
        v-if="$store.state.user == null"
        to="/prihlasenie"
        class="login button"
        :aria-label="$t('menu.prihlasitSa')"
      >
        <img
          @click="this.odhlasenie()"
          src="@/assets/images/icons/prihlasenie.svg"
          :alt="$t('menu.alt.login')"
        />
        <router-link to="/prihlasenie">
          {{ $t("menu.prihlasitSa") }}
        </router-link>
      </router-link>

      <div class="moj-ucet">
        <router-link
          v-if="$store.state.user != null"
          to="/ucebna"
          class="login no-padding button"
          :aria-label="$t('menu.mojaUcebna')"
        >
          <img
            :src="profilePhoto"
            :alt="$t('menu.alt.profilePhoto')"
            class="profile-icon"
          />
          <p>{{ $t("menu.mojaUcebna") }}</p>
          <a class="logout">
            <img
              @click="this.odhlasenie()"
              src="@/assets/images/icons/odhlasenie.svg"
              :alt="$t('menu.alt.logout')"
            />
          </a>
        </router-link>
      </div>
    </div>
  </section>
</template>

<script>
import defaultAvatar from "@/assets/images/icons/prihlasenie.svg";
import heartAddIcon from "@/assets/images/icons/heart-add.svg";
import heartRemoveIcon from "@/assets/images/icons/heart-remove.svg";
import doneAddIcon from "@/assets/images/icons/done-add.svg";
import doneRemoveIcon from "@/assets/images/icons/done-remove.svg";
import printIcon from "@/assets/images/icons/tlacit.svg";

export default {
  props: {
    song: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      hasNewSongsBadge: false,
      unregAfterEach: null, // (bez podčiarkovníka) odregistrujeme router hook pri unmount
      zoomLabel: "100%",
      isFavorite: false,
      isCompleted: false,
    };
  },

  computed: {
    profilePhotoRaw() {
      return this.$store.state.user?.profilePhotoUrl || "";
    },
    profilePhoto() {
      if (!this.profilePhotoRaw) return defaultAvatar;
      return this.stripWWW(this.profilePhotoRaw);
    },
    userKey() {
      const u = this.$store.state.user;
      if (!u) return "guest";
      return (
        (u.id != null && String(u.id)) ||
        (u.email && String(u.email).toLowerCase()) ||
        "guest"
      );
    },
    zapis() {
      const payload = this.song || {};
      return this.song?.zapis || payload;
    },
    availableScales() {
      const list =
        this.song?.stupnice ||
        this.zapis?.stupnice ||
        this.song?.zapis?.stupnice ||
        [];
      return Array.isArray(list) ? list : [];
    },
    activeScale() {
      return (
        this.song?.aktualnaStupnica ||
        this.zapis?.stupnica ||
        this.song?.stupnica ||
        ""
      );
    },
    genreLabel() {
      const beats = Number(this.zapis?.doby ?? this.song?.doby);
      if (!Number.isFinite(beats)) return "";
      if (beats === 3) return "Valčík";
      if (beats === 4) return "Polka";
      return "";
    },
    favoriteIcon() {
      return this.isFavorite ? heartRemoveIcon : heartAddIcon;
    },
    completedIcon() {
      return this.isCompleted ? doneRemoveIcon : doneAddIcon;
    },
    printIcon() {
      return printIcon;
    },
    productId() {
      const raw = this.song?.productID ?? this.zapis?.productID;
      const id = Number(raw);
      return Number.isFinite(id) ? id : null;
    },
    canPrint() {
      const user = this.$store.state.user;
      return Boolean(
        user?.edupage_in_calendar ||
          (user?.isAdmin &&
            this.$store.state.userRoles?.roles?.includes("admin_master"))
      );
    },
  },

  mounted() {
    this.safeEvaluateBadge();

    // auto-zhasnutie po vstupe do /ucebna/moje-piesne
    if (this.$router?.afterEach) {
      this.unregAfterEach = this.$router.afterEach((to) => {
        if (to?.path?.startsWith?.("/ucebna/moje-piesne")) {
          this.markSongsSeen();
        }
      });
    }
    if (typeof window !== "undefined") {
      window.addEventListener("ha-zoom-changed", this.handleZoomChanged);
      this.requestZoomState();
    }
  },

  beforeUnmount() {
    if (this.unregAfterEach) {
      try {
        this.unregAfterEach();
      } catch (e) {
        /* no-op */
      }
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("ha-zoom-changed", this.handleZoomChanged);
    }
  },

  watch: {
    "$store.state.user"() {
      this.safeEvaluateBadge();
    },
    "$store.state.user.ownedNotes.length"() {
      this.safeEvaluateBadge();
    },
    productId: {
      handler() {
        this.fetchFavoriteStatus();
        this.fetchCompletionStatus();
      },
      immediate: true,
    },
  },

  methods: {
    stripWWW(url) {
      if (!url) return url;
      try {
        const u = new URL(url);
        if (u.hostname.startsWith("www.")) u.hostname = u.hostname.slice(4);
        return u.toString();
      } catch (e) {
        return url.replace(/^https?:\/\/www\./, (m) => m.replace("www.", ""));
      }
    },

    odhlasenie() {
      this.$store.dispatch("odhlasenie");
    },

    getTotalCartItem() {
      return this.$store.state.userCart.reduce(
        (sum, item) => sum + item.count,
        0
      );
    },

    // ———— logika „Nové“ ————
    getOwnedCount() {
      const list = this.$store.state.user?.ownedNotes || [];
      return Array.isArray(list) ? list.length : 0;
    },
    readLastSeen() {
      try {
        return Number(
          localStorage.getItem(`ha_songs_last_seen_${this.userKey}`) || "0"
        );
      } catch (e) {
        /* no-op */ return 0;
      }
    },
    writeLastSeen(count) {
      try {
        localStorage.setItem(
          `ha_songs_last_seen_${this.userKey}`,
          String(count)
        );
      } catch (e) {
        /* no-op */
      }
    },
    setAlert(on) {
      this.hasNewSongsBadge = !!on;
      try {
        localStorage.setItem(`ha_songs_alert_${this.userKey}`, on ? "1" : "0");
      } catch (e) {
        /* no-op */
      }
    },

    evaluateBadge() {
      if (!this.$store.state.user) {
        this.setAlert(false);
        return;
      }

      const current = this.getOwnedCount();
      const lastSeen = this.readLastSeen();

      if (!lastSeen && current >= 0) {
        this.writeLastSeen(current);
        this.setAlert(false);
        return;
      }
      this.setAlert(current > lastSeen);
    },

    safeEvaluateBadge() {
      this.evaluateBadge();

      if (this.$store.state.user && this.getOwnedCount() === 0) {
        setTimeout(this.evaluateBadge, 300);
        setTimeout(this.evaluateBadge, 900);
      }
    },

    // klik / vstup do „moje-piesne“
    markSongsSeen() {
      if (!this.$store.state.user) return;
      const current = this.getOwnedCount();
      this.writeLastSeen(current);
      this.setAlert(false);
    },
    requestZoomState() {
      if (typeof window === "undefined") return;
      window.dispatchEvent(new Event("ha-zoom-request"));
    },
    handleZoomChanged(event) {
      const percent = event?.detail?.percent;
      if (typeof percent === "string" && percent) {
        this.zoomLabel = percent;
      }
    },
    emitZoomEvent(name) {
      if (typeof window === "undefined") return;
      window.dispatchEvent(new Event(name));
    },
    zoomIn() {
      this.emitZoomEvent("ha-zoom-increase");
    },
    zoomOut() {
      this.emitZoomEvent("ha-zoom-decrease");
    },
    toggleFavorite() {
      this.toggleFavoriteStatus();
    },
    toggleCompleted() {
      this.toggleCompletionStatus();
    },
    isActiveScale(scale) {
      return (
        String(scale || "").trim() === String(this.activeScale || "").trim()
      );
    },
    selectScale(scale) {
      if (!scale) return;
      if (this.isActiveScale(scale)) return;
      if (typeof window === "undefined") return;
      window.dispatchEvent(
        new CustomEvent("ha-stupnica-change", { detail: { scale } })
      );
    },
    getScaleLabel(element) {
      const mode = ["D", "A"].includes(element) ? "minor" : "major";
      return this.$t(`classroom.sheet.scale.${mode}`, { scale: element });
    },
    fetchCompletionStatus() {
      if (!this.productId) {
        this.isCompleted = false;
        return;
      }
      const axios = require("axios");
      axios
        .request({
          method: "get",
          maxBodyLength: Infinity,
          url:
            this.$store.state.api +
            "/user/finish/isProductsFinished.php/?id=" +
            this.productId,
        })
        .then((response) => {
          this.isCompleted = response?.data?.data !== "False";
        })
        .catch(() => {
          this.isCompleted = false;
        });
    },
    fetchFavoriteStatus() {
      if (!this.productId) {
        this.isFavorite = false;
        return;
      }
      const axios = require("axios");
      axios
        .request({
          method: "get",
          maxBodyLength: Infinity,
          url:
            this.$store.state.api +
            "/user/like/isProductsLiked.php/?id=" +
            this.productId,
        })
        .then((response) => {
          this.isFavorite = response?.data?.data !== "False";
        })
        .catch(() => {
          this.isFavorite = false;
        });
    },
    toggleCompletionStatus() {
      if (!this.productId) return;
      const axios = require("axios");
      const url = this.isCompleted
        ? this.$store.state.api +
          "/user/finish/unfinishProduct.php/?id=" +
          this.productId
        : this.$store.state.api +
          "/user/finish/finishProduct.php/?id=" +
          this.productId;
      axios
        .request({ method: "get", maxBodyLength: Infinity, url })
        .then((response) => {
          if (response?.data?.status === "Request succesfull") {
            this.fetchCompletionStatus();
          } else {
            this.$store.state.SnackBarText = this.$t(
              "classroom.sheet.notifications.completionUpdateFailed"
            );
          }
        })
        .catch(() => {
          this.$store.state.SnackBarText = this.$t(
            "classroom.sheet.notifications.genericError"
          );
        });
    },
    toggleFavoriteStatus() {
      if (!this.productId) return;
      const axios = require("axios");
      const url = this.isFavorite
        ? this.$store.state.api +
          "user/like/unlikeProduct.php/?id=" +
          this.productId
        : this.$store.state.api +
          "user/like/likeProduct.php/?id=" +
          this.productId;
      axios
        .request({ method: "get", maxBodyLength: Infinity, url, headers: {} })
        .then((response) => {
          if (response?.data?.status === "Request succesfull") {
            this.fetchFavoriteStatus();
          } else {
            this.$store.state.SnackBarText = this.$t(
              "classroom.sheet.notifications.favoriteUpdateFailed"
            );
          }
        })
        .catch(() => {
          this.$store.state.SnackBarText = this.$t(
            "classroom.sheet.notifications.genericError"
          );
        });
    },
    onPrintClick(e) {
      try {
        e && e.stopPropagation && e.stopPropagation();
      } catch (_) {
        /* no-op */
      }
      try {
        e && e.preventDefault && e.preventDefault();
      } catch (_) {
        /* no-op */
      }
      try {
        window.focus();
      } catch (_) {
        /* no-op */
      }
      window.print();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/sass/_colors.scss";
@import "@/assets/sass/globalStyles.scss";
.button.login.no-padding {
  padding: 0;
}

.profile-icon {
  border-top-left-radius: 50% !important;
  border-top-right-radius: 50% !important;
  border-bottom-left-radius: 50% !important;
  border-bottom-right-radius: 50% !important;
  width: 1.5em !important;
  height: 1.5em !important;
  padding: 0.5em !important;
  margin-right: 0 !important;
  object-fit: cover;
  filter: drop-shadow(3px 3px 3px rgba(0, 0, 0, 0.3));
}

.menu {
  height: 100%;
  width: var(--menu-width);
  max-width: var(--menu-width);
  min-width: var(--menu-width);
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  max-height: 100vh;
  max-height: 100dvh;
  overflow: hidden;
  font-size: clamp(0.82rem, 0.9vw, 1rem);
  flex: 0 0 auto;
  flex-shrink: 0;
}

:global(#app > .container) {
  flex: 1 1 0;
  min-width: 0;
}

.logo {
  height: 30%;
}

.navigation {
  height: 100%;
  padding-bottom: 0.8rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
}

.bot-nav {
  width: 100%;
  flex-shrink: 0;
  padding-top: 0.25rem;
  position: relative;
  border-top: 1px solid rgba(31, 44, 79, 0.08);
}

.bot-nav::before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  top: -1rem;
  height: 1rem;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    rgba(237, 237, 237, 0),
    rgba(237, 237, 237, 0.7),
    #ededed
  );
}
.bot-nav .zoom-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
}
.bot-nav .zoom-panel {
  display: flex;
  justify-content: center;
  width: 40%;
  margin: 0;
}
.zoom-toggle {
  width: 2.4rem;
  height: 2.4rem;
  padding: 0;
  border-radius: 0.92857rem;
  border: none;
  background: #fef35a;
  box-shadow: 3px 3px 3px 0px rgba(0, 0, 0, 0.25);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.15s ease;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}

.zoom-toggle:active {
  transform: translateY(0) scale(0.96);
}
.zoom-toggle:hover,
.zoom-toggle:focus-visible {
  transform: translateY(-1px);
}
.zoom-toggle img {
  margin-top: 0.2em;
  margin-left: 0.1em;
  width: 1.25rem;
  height: 1.25rem;
  pointer-events: none;
}
.zoom-panel {
  display: inline-flex;
  align-items: stretch;
  background: #fef35a;
  box-shadow: 3px 3px 3px 0px rgba(0, 0, 0, 0.25);
  border-radius: 1rem;
  padding: 0.15rem;
}
.zoom-panel .zoom-edge {
  width: 2rem;
  height: 2rem;
  border: none;
  background: transparent;
  color: #1f2c4f;
  font-size: 1.4rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}
.zoom-panel .zoom-edge.left {
  border-top-left-radius: 0.6rem;
  border-bottom-left-radius: 0.6rem;
}
.zoom-panel .zoom-edge.right {
  border-top-right-radius: 0.6rem;
  border-bottom-right-radius: 0.6rem;
}
.zoom-panel .zoom-edge:hover,
.zoom-panel .zoom-edge:focus-visible {
  background: #fef35a;
  color: #000;
}

.zoom-panel .zoom-edge:active {
  transform: scale(0.96);
}
.zoom-panel .zoom-value {
  font-size: 0.72rem;
  font-weight: 700;
  color: #1f2c4f;
  min-width: 2.4rem;
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: "Bahnschrift", sans-serif;
}

@keyframes toggle-pop {
  0% {
    transform: translateY(0) scale(1);
  }
  60% {
    transform: translateY(-1px) scale(1.08);
  }
  100% {
    transform: translateY(0) scale(1);
  }
}

.zoom-toggle[aria-pressed="true"] {
  animation: toggle-pop 0.28s ease-out;
}

.green {
  background: #8ec84e;
}
.top-nav {
  width: 100%;
}

.logo img {
  width: 83%;
  margin: 1vw auto auto;
  filter: drop-shadow(3px 3px 3px rgba(0, 0, 0, 0.3));
}

ul {
  display: flex;
  flex-direction: column;
  align-items: center;
  a.login {
    font-size: 120%;
  }
}

.cart-number {
  background: #fef35a;
  position: absolute;
  border-radius: 20rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.55em;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.35);
  top: -0.3em;
  left: 1.15em;
  padding: 0.2em 0.5em 0.1em;
}

.navigation > div.bot-nav > ul > a:nth-child(4) {
  margin-top: 0.4em;
}

.navigation li {
  padding: 1.5% 2.5% 1.5% 5%;
  margin: 1% auto;
  display: flex;
  align-items: center;
  gap: 6%;
  position: relative;
  font-size: 1.5625em;
  font-style: normal;
  font-weight: 700;
  letter-spacing: 0.03em;
  width: 13.5rem;

  &:hover {
    background: lighten($lt-gr-clr, 20%);
    transition-duration: 0.2s;
    border-radius: 0.625em;

    img {
      filter: brightness(0);
    }
  }

  img {
    width: 5em;
  }
  a {
    color: #000;
  }

  & > a {
    display: flex;
    gap: 6%;
    justify-content: flex-start;
    align-items: center;
  }
}

.icon {
  width: 10%;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    height: 100%;
    width: 1em;
  }
}

.login {
  padding: 0.4em 0.8em;
  img {
    width: 1.4em;
  }
}

.prihlaseny {
  padding: 0.4em 1.5em;
}

.login.no-padding {
  img {
    width: 1em;
    padding: 0.6em 0 0.6em 0.8em;
  }
  p {
    padding: 0.6em 0.4em 0.6em 0;
  }
}

li:has(.router-link-active.router-link-exact-active) {
  background: $lt-gr-clr;
  border-radius: 0.625em;
  img {
    filter: brightness(0);
  }
}

section {
  padding: 0;
}

.moj-ucet .login .logout {
  height: 100%;
  fill: #fef35a;
  background: url("@/assets/images/icons/elipsaOdhlasenie.svg");
  padding-right: 0.6em;
  background-position: center;
  background-size: cover;
  margin-bottom: 0 !important;
  transition-duration: 0.2s;
}
.moj-ucet .login .logout:hover {
  transform: scale(1.1);
}
.moj-ucet .login .logout img {
  width: 0.95em;
  margin-right: 0;
}
.moj-ucet p {
  font-size: 0.8em;
}

@media only screen and (max-width: 1500px) {
  .moj-ucet {
    font-size: 90%;
  }
}
@media only screen and (max-height: 700px) {
  .logo img {
    width: 70%;
  }
}

@media (max-height: 860px) {
  .menu {
    font-size: 0.88rem;
  }

  .song-text-wrapper {
    max-height: 35vh;
  }
}

@media (max-height: 760px) {
  .menu {
    font-size: 0.82rem;
  }
}

/* ——— vizuál „Nové“ prilepený k žltému buttonu ——— */
.menu-link-with-badge {
  position: relative; /* referenčný box pre badge */
}
.ha-new-badge {
  font-family: "Bahnschrift", sans-serif;
  position: absolute;
  top: -1em;
  right: -0.1em;
  padding: 0.18em 0.65em 0.12em;
  border-radius: 1em;
  background: #ff9900; /* oranžové pozadie */
  color: #ffffff; /* biely text */
  font-weight: 700;
  font-size: 0.9em;
  letter-spacing: 0.02em;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  outline: 2px solid rgba(255, 255, 255, 0.6);
  outline-offset: -2px;
}

h1 {
  color: #fef35a;
  text-align: center;
  text-shadow: 5px 5px 5px rgba(0, 0, 0, 0.25);
  -webkit-text-stroke-width: 0.035em;
  -webkit-text-stroke-color: #000;
  font-family: Adumu;
  font-size: 2.5em;
  font-style: normal;
  font-weight: 400;
  line-height: 115%; /* 2.875rem */
  margin: 0.5em auto 0.2em;
}

h5 {
  color: #000;

  text-align: center;
  font-family: Bahnschrift;
  font-size: 1.25em;
  font-style: normal;
  font-weight: 700;
  line-height: 115%; /* 1.4375rem */
  margin: 0 auto 0.3em;
}

.info-text {
  text-align: center;

  font-size: 1em;
  font-style: normal;
  font-weight: 400;
  line-height: 115%;
  /* 1.79688rem */
  margin-top: 0.2em;
  margin-bottom: 0.4em;
}

.line {
  width: 0.25rem;
}

.line.horizontal {
  height: 0.3rem;
  margin-bottom: 0.6em;
}
.line.horizontal.last-line {
  margin-bottom: 0.7rem !important;
  margin-top: 0 !important;
}

.dificulty {
  transform: rotate(180deg);
  margin: auto;
  margin-top: -2.1em; //-1em
  width: 6em;
}
.dificulty img {
  width: 70%;
}

.strong {
  color: #686868;
  text-align: center;
  font-family: Bahnschrift;
  font-size: 0.875em;
  font-style: normal;
  font-weight: 400;
  line-height: 115%; /* 1.00625rem */
}

.info-box {
  display: flex;
  justify-content: center;
  gap: 1vw;
  margin: 0.6em 0;
  align-items: stretch;
  margin-right: 1em;

  .line {
    height: auto;
  }

  .info:first-child {
    max-width: 40%;
  }
}

.bmp-box {
  display: flex;
  align-items: center;
  gap: 0.4em;
  justify-content: center;
  margin-top: 0.3em;
  margin-bottom: 0.6em;
}
.bmp-box img {
  width: 15%;
}
.bmp-box .info-text {
  margin: 0;
}

p.info-text.bmp {
  margin-top: 0.2em;
}

.info-buttons {
  display: flex;
  align-items: center;
  font-size: 50%;
  gap: 2em;
  margin: 1.2em;
  flex-direction: row;
  justify-content: center;

  .button {
    margin-bottom: 0.5em;
  }
}

.last-line {
  margin-bottom: 1.6em;
}
.song-info-box {
  transform-origin: top center;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.difficulty-row {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
  gap: 0.75rem;
  margin: 0.1rem 0 0.5rem;
}

.sheet-print {
  position: absolute;
  right: 0.8rem;
  top: 0.1rem;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
}

.sheet-print .tlac-img {
  background-color: #fef35a;
  box-shadow: 0.1em 0.1em 1em #00000040;
  border-radius: 0.6rem;
  padding: 0.35em 0.5em;
  width: 1.2em;
  height: 1.2em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.sheet-print .tlac-img:hover,
.sheet-print .tlac-img:focus-visible {
  transform: translateY(-1px);
  box-shadow: 0.2em 0.2em 0.8em #00000050;
}

.sheet-print img {
  width: 1.1em;
  height: 1.1em;
}

.sheet-print .print-label {
  font-size: 0.58rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #1f2c4f;
}

.song-text-wrapper {
  flex: 1;
  min-height: 0;
  width: 100%;
  max-height: none;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.35rem;
  box-sizing: border-box;
  scrollbar-width: thin;
  scrollbar-color: rgba(31, 44, 79, 0.35) transparent;
}

.song-info-box > .info:last-child {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1 1 auto;
}

.song-text-wrapper::-webkit-scrollbar {
  width: 6px;
}

.song-text-wrapper::-webkit-scrollbar-thumb {
  background: rgba(31, 44, 79, 0.35);
  border-radius: 8px;
}

.text-song {
  margin: 0;
  white-space: pre-line;
}

.info-box-enter-active,
.info-box-leave-active {
  transition: all 0.3s ease;
}

.info-box-enter-from,
.info-box-leave-to {
  opacity: 0;
  transform: scale(0.8) translateY(-20px);
}

.info-box-enter-to,
.info-box-leave-from {
  opacity: 1;
  transform: scale(1) translateY(0);
}

.add-favorite {
  position: fixed;
  width: max-content !important;
  bottom: 1em;
  left: 0.8vw;
  width: 3em;
  filter: drop-shadow(4px 3px 7px rgba(0, 0, 0, 0.25));

  display: flex;
  flex-direction: column;
  gap: 0.4em;
  align-items: center;

  img {
    width: 3.3em;
    cursor: pointer;

    &:hover {
      transform: scale(1.1);
      transition-duration: 0.2s;
    }
  }
}

.add-favorite.right {
  left: unset;
  right: 0.8vw;
}
</style>
